import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Users, Plus, Trophy, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

export default function Challenges() {
  const [params, setParams] = useSearchParams();
  const [challenges, setChallenges] = useState([]);
  const [selected, setSelected] = useState(null);
  const [board, setBoard] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [form, setForm] = useState({ name: "", target_amount: "", duration_weeks: 4, category: "savings" });
  const [joinCode, setJoinCode] = useState("");
  const [copied, setCopied] = useState(null);

  const load = async () => {
    const r = await api.get("/challenges");
    setChallenges(r.data);
    if (r.data.length && !selected) setSelected(r.data[0]);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (params.get("code")) { setJoinCode(params.get("code").toUpperCase()); setShowJoin(true); }
  }, [params]);

  useEffect(() => {
    if (!selected) return;
    api.get(`/challenges/${selected.challenge_id}/leaderboard`).then(r => setBoard(r.data)).catch(() => {});
  }, [selected]);

  const create = async (e) => {
    e.preventDefault();
    const r = await api.post("/challenges", { ...form, target_amount: parseFloat(form.target_amount), duration_weeks: parseInt(form.duration_weeks) });
    toast.success(`Challenge created: ${r.data.code}`);
    setShowCreate(false); setForm({ name: "", target_amount: "", duration_weeks: 4, category: "savings" });
    load(); setSelected(r.data);
  };

  const join = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/challenges/join", { code: joinCode });
      toast.success(`Joined ${r.data.name}`);
      setShowJoin(false); setJoinCode("");
      setParams({});
      load(); setSelected(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to join"); }
  };

  const copyCode = async (code) => {
    const url = `${window.location.origin}/challenges?code=${code}`;
    await navigator.clipboard.writeText(url);
    setCopied(code); toast.success("Invite link copied");
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-6 h-6 text-[color:var(--gold)]" />
            <h1 className="font-heading text-3xl font-bold">Group Challenges</h1>
          </div>
          <p className="text-[color:var(--text-secondary)]">Save more, together. Invite friends via a shareable code.</p>
        </div>
        <div className="flex gap-3">
          <button data-testid="join-challenge-btn" onClick={() => setShowJoin(true)} className="px-5 py-2.5 rounded-full border border-[rgba(212,175,55,0.3)] hover:border-[color:var(--gold)] text-sm transition">
            Join with code
          </button>
          <button data-testid="create-challenge-btn" onClick={() => setShowCreate(true)} className="gold-btn px-5 py-2.5 rounded-full inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New challenge
          </button>
        </div>
      </div>

      {!challenges.length ? (
        <div className="glass-card p-16 text-center">
          <Trophy className="w-12 h-12 mx-auto text-[color:var(--gold)] mb-4" />
          <h2 className="font-heading text-xl font-semibold mb-2">No challenges yet</h2>
          <p className="text-[color:var(--text-secondary)] text-sm mb-6">Start a 4-week savings sprint or join a friend's challenge.</p>
          <button onClick={() => setShowCreate(true)} className="gold-btn px-6 py-3 rounded-full">Create your first challenge</button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            {challenges.map(ch => (
              <button key={ch.challenge_id} data-testid={`ch-${ch.challenge_id}`} onClick={() => setSelected(ch)}
                className={`w-full text-left p-4 rounded-2xl border transition ${selected?.challenge_id === ch.challenge_id ? "border-[color:var(--gold)] bg-[rgba(212,175,55,0.08)]" : "border-[rgba(212,175,55,0.12)] hover:border-[rgba(212,175,55,0.3)]"}`}>
                <div className="font-medium text-sm">{ch.name}</div>
                <div className="text-xs text-[color:var(--text-muted)] mt-1">${ch.target_amount.toLocaleString()} · {ch.duration_weeks}w · {ch.participants.length} joined</div>
                <div className="font-mono text-[10px] text-[color:var(--gold)] mt-1">{ch.code}</div>
              </button>
            ))}
          </div>

          {selected && board && (
            <motion.div key={selected.challenge_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2 glass-card p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="font-heading text-2xl font-bold">{selected.name}</h2>
                  <div className="text-xs text-[color:var(--text-muted)] mt-1 uppercase tracking-widest">{selected.category} · {selected.duration_weeks} weeks · target ${selected.target_amount.toLocaleString()}</div>
                </div>
                <button data-testid={`copy-code-${selected.code}`} onClick={() => copyCode(selected.code)}
                  className="px-3 py-2 rounded-full border border-[rgba(212,175,55,0.25)] hover:border-[color:var(--gold)] text-xs font-mono inline-flex items-center gap-2 transition">
                  {copied === selected.code ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {selected.code}
                </button>
              </div>
              <div className="space-y-2">
                {board.leaderboard.map((row) => (
                  <motion.div key={row.user_id} initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }}
                    className={`flex items-center gap-4 p-3 rounded-xl ${row.is_you ? "bg-[rgba(212,175,55,0.08)]" : "bg-[rgba(255,255,255,0.02)]"}`}>
                    <div className="w-8 text-center">
                      {row.rank === 1 ? <Trophy className="w-4 h-4 text-[color:var(--gold)] mx-auto" /> : <span className="font-mono font-bold text-[color:var(--text-muted)]">#{row.rank}</span>}
                    </div>
                    <div className="w-9 h-9 rounded-full bg-[rgba(212,175,55,0.15)] flex items-center justify-center text-[color:var(--gold)] font-semibold text-sm">{row.initial}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium flex items-center gap-2">
                        {row.name}
                        {row.is_you && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(212,175,55,0.15)] text-[color:var(--gold)] uppercase tracking-widest">You</span>}
                      </div>
                      <div className="h-1.5 rounded-full bg-[rgba(255,255,255,0.05)] mt-1.5 overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${row.pct}%` }} transition={{ duration: 1 }}
                          className="h-full bg-gradient-to-r from-[#D4AF37] to-[#E9C46A]" />
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold gold-gradient-text">${row.progress.toLocaleString()}</div>
                      <div className="text-[10px] text-[color:var(--text-muted)]">{row.pct}%</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <motion.form initial={{ scale: 0.9 }} animate={{ scale: 1 }} onSubmit={create}
            className="glass-card p-8 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold">New challenge</h2>
              <button type="button" onClick={() => setShowCreate(false)}><X className="w-5 h-5 text-[color:var(--text-muted)]" /></button>
            </div>
            <input data-testid="ch-name" required placeholder="Challenge name (e.g. Summer Savings Sprint)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none" />
            <input data-testid="ch-target" required type="number" placeholder="Target amount ($)" value={form.target_amount} onChange={e => setForm({ ...form, target_amount: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none" />
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="ch-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] outline-none">
                <option value="savings">Savings</option>
                <option value="debt">Debt Payoff</option>
              </select>
              <select data-testid="ch-duration" value={form.duration_weeks} onChange={e => setForm({ ...form, duration_weeks: e.target.value })}
                className="px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] outline-none">
                {[2, 4, 8, 12].map(n => <option key={n} value={n}>{n} weeks</option>)}
              </select>
            </div>
            <button data-testid="ch-submit" type="submit" className="gold-btn w-full py-3 rounded-xl">Create & get code</button>
          </motion.form>
        </div>
      )}

      {showJoin && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowJoin(false)}>
          <motion.form initial={{ scale: 0.9 }} animate={{ scale: 1 }} onSubmit={join}
            className="glass-card p-8 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold">Join a challenge</h2>
              <button type="button" onClick={() => setShowJoin(false)}><X className="w-5 h-5 text-[color:var(--text-muted)]" /></button>
            </div>
            <input data-testid="ch-join-code" required placeholder="CH-XXXXXX" value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())}
              className="w-full px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none font-mono uppercase" />
            <button data-testid="ch-join-submit" type="submit" className="gold-btn w-full py-3 rounded-xl">Join</button>
          </motion.form>
        </div>
      )}
    </div>
  );
}
